<template>
  <div v-if="errorLoading" class="p-4">Unable to generate preview.</div>
  <iframe v-else ref="previewEl" />
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { onMounted, ref, watch } from "vue";

import { injectUseEmailTemplates } from "./useEmailTemplates";

const { previewEmailTemplate, selectedTemplateKey, templateCustomizations } =
  injectUseEmailTemplates();
let initCustomizationsForTemplate = false;
const loading = ref(false);
const errorLoading = ref(false);
const previewEl = ref<any>();
const currentTemplateKey = ref<any>();

// When we switch templates, we want to update the entire iframe layout.
// When we just update the content, we only swap what's inside the <body> element
function replaceIframeContent(content: string, replaceLayout = false) {
  const doc = new DOMParser().parseFromString(content, "text/html");
  const iframeDoc = previewEl.value!.contentWindow.document as Document;
  if (replaceLayout) {
    iframeDoc.querySelector("head")!.innerHTML = `
      ${doc.querySelector("head")!.innerHTML}
        <style>
        a, button, input { pointer-events: none; }
        </style>
    `;
  }
  iframeDoc.querySelector("body")!.innerHTML =
    doc.querySelector("body")?.innerHTML || "";
}

async function handleUpdatePreviewEmailTemplate() {
  loading.value = true;
  errorLoading.value = false;
  try {
    const results = await previewEmailTemplate();
    replaceIframeContent(
      results.content,
      currentTemplateKey.value !== results.templateKey
    );
    currentTemplateKey.value = results.templateKey;
  } catch (e) {
    errorLoading.value = true;
  } finally {
    loading.value = false;
  }
}

const updatePreviewContentDebounced = debounce(
  handleUpdatePreviewEmailTemplate,
  400
);

onMounted(() => {
  handleUpdatePreviewEmailTemplate();
});

watch(
  () => ({
    templateKey: selectedTemplateKey.value,
    content: JSON.stringify(templateCustomizations.value)
  }),
  (oldVal, newVal) => {
    // Immediately update if template key changes
    if (oldVal.templateKey !== newVal.templateKey) {
      initCustomizationsForTemplate = true;
      return handleUpdatePreviewEmailTemplate();
    }

    // If the content changes but we already retrieved it via the template key
    // change logic, don't fetch again.
    if (oldVal.content !== newVal.content) {
      if (initCustomizationsForTemplate) {
        initCustomizationsForTemplate = false;
        return;
      }
      updatePreviewContentDebounced();
    }
  }
);
</script>

<style lang="scss" scoped>
iframe {
  border: none;
}
</style>
